/* eslint-disable no-template-curly-in-string */
// 灵感 发现/关注/分类导航
import useAppStore from "@/context/store";
import { useSafeState } from "ahooks";
import classNames from "classnames";
import { useEffect, useMemo, useRef } from "react";
import { When } from "react-if";
import HBIcon from "../HBIcon";
import HBSwiper from "../HBSwiper";
import styles from "./index.module.less";

const VISIBLE_ITEMS = 7; // 收起时显示的item数量

export default function HBCategoryNav({
  onChange,
  value,
  options,
  allText = "最新",
  extraOption,
  hideAll = false,
  filterValues = [], // 过滤掉不需要的分类
  baseHref,
}) {
  const { categories: appCategories } = useAppStore(({ categories }) => ({
    categories,
  }));
  const [activeOption, setActiveOption] = useSafeState(null);
  const [showAll, setShowAll] = useSafeState(false);
  const swiperRef = useRef(null);
  const hasScrolledRef = useRef(false);

  const categories = useMemo(() => {
    let categories = options || appCategories;
    if (filterValues.length > 0) {
      categories = categories.filter(
        (category) => !filterValues.includes(category.value)
      );
    }
    return categories;
  }, [options, appCategories, filterValues]);

  useEffect(() => {
    if (value === null) {
      setActiveOption(null);
    } else if (extraOption?.value && value === extraOption?.value) {
      setActiveOption({ value: extraOption?.value, name: extraOption?.text });
    } else if (value && categories.length > 0) {
      const activeCategoryOption = categories.find(
        (option) => option.value === value
      );
      setActiveOption(activeCategoryOption);
    } else {
      setActiveOption(undefined);
    }

    // 当选中项变化时，滚动到选中项的位置
    if (
      swiperRef.current &&
      showAll &&
      categories.length > VISIBLE_ITEMS &&
      !hasScrolledRef.current
    ) {
      const activeIndex = categories.findIndex((item) => item.value === value);
      if (activeIndex !== -1) {
        // 使用setTimeout确保在下一个事件循环中执行滚动，确保dom&动画完成
        const timerId = setTimeout(() => {
          if (swiperRef.current) {
            swiperRef.current.onGoPage({ index: activeIndex });
            hasScrolledRef.current = true;
          }
        }, 300);

        // 清理函数，在组件卸载或依赖项改变时清除定时器
        return () => clearTimeout(timerId);
      }
    }
  }, [value, categories, setActiveOption, extraOption, showAll]);

  // 当showAll状态改变时，重置滚动标志
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [showAll]);

  // 检查选中项是否在可见范围内
  useEffect(() => {
    if (activeOption && categories.length > VISIBLE_ITEMS) {
      const activeIndex = categories.findIndex(
        (item) => item.value === activeOption.value
      );
      if (activeIndex >= VISIBLE_ITEMS) {
        setShowAll(true);
      }
    }
  }, [activeOption, categories, hideAll, extraOption]);

  const onClick = (evt, option) => {
    evt.preventDefault();
    setActiveOption(option);
    onChange && onChange(option);
  };

  const onToggle = () => {
    setShowAll(!showAll);
  };

  const renderItem = (option, isExtra = false) => (
    <a
      key={option?.value || "all"}
      data-gd-click="button_click"
      data-button-name={option?.name || option?.text || allText}
      data-module-name="页面切换tab"
      data-subject-name="按钮"
      className={classNames([
        styles.item,
        (
          isExtra
            ? value === option?.value
            : activeOption?.value === option?.value
        )
          ? styles.active
          : null,
      ])}
      {...((
        isExtra
          ? value === option?.value
          : activeOption?.value === option?.value
      )
        ? { id: "active-filter-item" }
        : {})}
      {...(baseHref && option?.value
        ? {
            href: isExtra
              ? baseHref.replace("discovery", option?.value)
              : baseHref + "/" + option?.value,
          }
        : baseHref
        ? { href: baseHref }
        : null)}
      onClick={(evt) =>
        onClick(
          evt,
          isExtra ? { value: option?.value, name: option?.text } : option
        )
      }
    >
      {option?.name || option?.text || allText}
    </a>
  );

  const visibleCategories = categories.slice(0, VISIBLE_ITEMS);
  const hasMore = categories.length > VISIBLE_ITEMS;

  const renderDefaultItems = () => (
    <>
      <When condition={!hideAll}>
        {renderItem(null)}
        {extraOption &&
          extraOption?.text &&
          extraOption?.value &&
          renderItem(extraOption, true)}
      </When>
      {visibleCategories.map((option) => renderItem(option))}
    </>
  );

  const renderSwiperItems = () => (
    <HBSwiper
      ref={swiperRef}
      className={styles.swiper}
      iconClassName={styles.swiperIcon}
      padding={20}
    >
      <When condition={!hideAll}>
        {renderItem(null)}
        {extraOption &&
          extraOption?.text &&
          extraOption?.value &&
          renderItem(extraOption, true)}
      </When>
      {categories.map((option) => renderItem(option))}
    </HBSwiper>
  );

  return (
    <div className={styles["category-nav"]}>
      <div
        className={classNames([
          styles.container,
          { [styles.expanded]: showAll },
        ])}
      >
        {showAll ? renderSwiperItems() : renderDefaultItems()}
        <When condition={hasMore && !showAll}>
          <div
            className={classNames([styles.item, styles.more])}
            onClick={onToggle}
          >
            <HBIcon type="ic_fold" rotate={90} />
          </div>
        </When>
      </div>
    </div>
  );
}
